import { useEffect } from 'react';
import Cookies from 'js-cookie';
import { Alert, AlertIcon, Button, FormControl, FormLabel, Heading, Input, Stack, Text } from '@chakra-ui/react';
import { Title } from '../../../atoms/Title';

export interface LoginProps {
  errors: Array<string>;
  samlEnabled?: boolean;
  username?: string;
  password?: string;
  isStudentApp?: boolean;
  onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLoginClick: () => void;
  onForgotPasswordClick: () => void;
  setIsLoading: (val: boolean) => void;
}

const Login = (props: LoginProps) => {
  const {
    errors,
    samlEnabled,
    username,
    password,
    isStudentApp,
    onUsernameChange,
    onPasswordChange,
    onLoginClick,
    onForgotPasswordClick,
    setIsLoading
  } = props;

  const handleLogin = e => {
    e.preventDefault();
    setIsLoading(true);
    onLoginClick();
  };

  useEffect(() => {
    const clearCookies = () => {
      const allCookies = Cookies.get();
      Object.keys(allCookies).forEach(cookieName => {
        Cookies.remove(cookieName, { domain: window.location.hostname });
      });

      /*
        Remove after doc split release
        We now set cookies to the subdomain, but during the doc split release the browser can still have the
        old cookies with just the domain name.
        If you are reading this and the code is still here, please remove the below, it is not needed.
      */
      const regex = /^.+(\.cas-(sheild|staff)\.(net|com))$/;
      const domain = window.location.hostname;
      const superDomain = domain.match(regex)?.[1];
      Object.keys(allCookies).forEach(cookieName => {
        Cookies.remove(cookieName, { domain: superDomain });
      });
      /* END remove */
    };

    clearCookies();
  }, []);

  const title = isStudentApp ? 'Log in' : 'Log in to CAS Shield';

  return (
    <>
      <Title title={title} />

      <Heading as="h1" mb="6">
        {title}
      </Heading>

      {errors.length > 0 ? (
        <Alert status="error" mb="4">
          <AlertIcon />
          {errors}
        </Alert>
      ) : null}

      {!samlEnabled ? (
        <form onSubmit={handleLogin}>
          <FormControl mb="4">
            <FormLabel fontSize="lg" htmlFor="username">
              {isStudentApp ? 'Email address' : 'Username'}
            </FormLabel>

            <Input
              id="username"
              autoComplete={isStudentApp ? 'email' : 'username'}
              type={isStudentApp ? 'email' : 'text'}
              size="lg"
              onChange={onUsernameChange}
              value={username}
              isRequired
              data-sentry-mask
            />
          </FormControl>

          <FormControl mb="8">
            <FormLabel fontSize="lg" htmlFor="current-password">
              Password
            </FormLabel>

            <Input
              id="current-password"
              autoComplete="current-password"
              size="lg"
              type="password"
              onChange={onPasswordChange}
              value={password}
              isRequired
              data-sentry-mask
            />
          </FormControl>

          <Stack spacing="3">
            <Button
              id="login"
              type="submit"
              size="lg"
              variant="solid"
              isDisabled={username?.trim() === '' || password.trim() === ''}
            >
              Log in
            </Button>

            <Button size="lg" variant="ghost" onClick={onForgotPasswordClick}>
              Reset password
            </Button>
          </Stack>
        </form>
      ) : (
        <Text>Please wait while you are redirected to log in via your identity provider.</Text>
      )}
    </>
  );
};

export default Login;
